<template>
  <div class="inspections-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Inspekcje</h1>
      </div>
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab>
        Do przeglądu
        <v-chip v-if="pendingReviews.length" x-small color="warning" text-color="white" class="ml-2">{{ pendingReviews.length }}</v-chip>
      </v-tab>
      <v-tab>
        Wezwania biurowe
        <v-chip v-if="summons.length" x-small color="primary" text-color="white" class="ml-2">{{ summons.length }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" touchless>
      <!-- ===== KOLEJKA: inspekcje do przeglądu (self-inspekcje kierowców) ===== -->
      <v-tab-item>
        <div class="d-flex align-center mb-3">
          <div class="grey--text">Inspekcje zgłoszone przez kierowców, oczekujące na akceptację.</div>
          <v-spacer></v-spacer>
          <v-btn small icon @click="fetchPendingReviews" title="Odśwież"><v-icon>mdi-refresh</v-icon></v-btn>
          <v-progress-circular v-if="loadingReviews" indeterminate size="20" width="2" color="primary" class="ml-2"></v-progress-circular>
        </div>
        <div v-if="!loadingReviews && !pendingReviews.length" class="grey--text pa-4">Brak inspekcji do przeglądu.</div>
        <v-card v-for="insp in pendingReviews" :key="insp.id" outlined class="mb-3 pa-3" :class="anomalyMeta(insp) ? 'insp-anomaly' : ''">
          <div class="d-flex align-center flex-wrap" style="gap:8px;">
            <v-chip x-small color="warning" text-color="white">Do przeglądu</v-chip>
            <v-chip v-if="anomalyMeta(insp)" x-small :color="anomalyMeta(insp).color" text-color="white">
              <v-icon left x-small>{{ anomalyMeta(insp).icon }}</v-icon>{{ anomalyMeta(insp).label }}
            </v-chip>
            <span class="font-weight-bold">{{ insp.car && insp.car.plate }}</span>
            <span class="grey--text">{{ insp.car && insp.car.make }} {{ insp.car && insp.car.model }}</span>
            <span class="grey--text" v-if="insp.driver">· {{ insp.driver.firstName }} {{ insp.driver.lastName }}</span>
            <span class="ml-2">{{ formatDateTime(insp.createdAt) }}</span>
            <v-spacer></v-spacer>
            <v-btn small color="success" :loading="reviewingId === insp.id" @click="review(insp.id, 'ACCEPT')" class="mr-1">Akceptuj</v-btn>
            <v-btn small color="error" :loading="reviewingId === insp.id" @click="review(insp.id, 'FLAG')">Oflaguj</v-btn>
          </div>
          <div v-if="inspMileage(insp)" class="mt-1 caption">
            Przebieg: <b>{{ inspMileage(insp) }} km</b>
            <template v-if="inspPrevOdometer(insp)">
              <span class="grey--text"> · poprzedni: {{ inspPrevOdometer(insp) }} km</span>
              <span :class="inspDelta(insp) < 0 ? 'red--text' : (anomalyMeta(insp) ? 'deep-orange--text' : 'grey--text')" class="font-weight-bold">
                ({{ inspDelta(insp) >= 0 ? '+' : '' }}{{ inspDelta(insp) }} km)
              </span>
            </template>
          </div>
          <div v-if="inspRatings(insp).length" class="mt-2 d-flex" style="gap:6px; flex-wrap:wrap;">
            <v-chip v-for="(r, i) in inspRatings(insp)" :key="i" x-small outlined :color="r.color">{{ r.name }}: <b class="ml-1">{{ r.label }}</b></v-chip>
          </div>
          <div v-if="insp.note" class="mt-1 caption">Uwagi kierowcy: {{ insp.note }}</div>
          <div v-if="insp.photos && insp.photos.length" class="mt-2 d-flex" style="gap:8px; flex-wrap:wrap;">
            <a v-for="(p, i) in insp.photos" :key="i" v-show="photoUrls[p]" :href="photoUrls[p]" target="_blank">
              <img :src="photoUrls[p]" style="height:96px; width:96px; object-fit:cover; border-radius:6px; border:1px solid #ddd;" />
            </a>
            <span v-if="!insp.photos.some((p) => photoUrls[p])" class="caption grey--text align-self-center">Ładowanie zdjęć…</span>
          </div>
        </v-card>
      </v-tab-item>

      <!-- ===== WEZWANIA BIUROWE ===== -->
      <v-tab-item>
        <v-card outlined class="mb-4 pa-4">
          <div class="section-label mb-3" style="font-weight:700; text-transform:uppercase; font-size:12px; color:#6E7691;">Wystaw wezwanie na inspekcję w biurze</div>
          <v-row>
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="summonForm.carId"
                :items="carOptions"
                label="Auto (kierowca)"
                outlined dense
                prepend-inner-icon="mdi-car"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="summonForm.deadline"
                type="datetime-local"
                label="Termin stawienia się"
                outlined dense
                prepend-inner-icon="mdi-calendar-clock"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="summonForm.note" label="Notatka (opcjonalnie)" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <div class="d-flex align-center">
            <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
            <span class="caption grey--text">Po minięciu terminu bez wykonanej inspekcji wypłaty kierowcy zostaną zablokowane (self-service i rozliczenia).</span>
            <v-spacer></v-spacer>
            <v-btn color="primary" :loading="summonSaving" :disabled="!summonForm.carId || !summonForm.deadline" @click="submitSummon">
              <v-icon left small>mdi-clipboard-alert-outline</v-icon>Wezwij
            </v-btn>
          </div>
        </v-card>

        <div class="d-flex align-center mb-2">
          <div class="section-label" style="font-weight:700; text-transform:uppercase; font-size:12px; color:#6E7691;">Aktywne wezwania</div>
          <v-spacer></v-spacer>
          <v-btn small icon @click="fetchSummons" title="Odśwież"><v-icon>mdi-refresh</v-icon></v-btn>
          <v-progress-circular v-if="loadingSummons" indeterminate size="20" width="2" color="primary" class="ml-2"></v-progress-circular>
        </div>
        <div v-if="!loadingSummons && !summons.length" class="grey--text pa-4">Brak aktywnych wezwań.</div>
        <v-card v-for="s in summons" :key="s.carId" outlined class="mb-2 pa-3">
          <div class="d-flex align-center flex-wrap" style="gap:8px;">
            <v-chip x-small :color="s.overdue ? 'error' : 'warning'" text-color="white">
              {{ s.overdue ? 'Po terminie — blokada wypłat' : 'Oczekuje' }}
            </v-chip>
            <span class="font-weight-bold">{{ s.plate }}</span>
            <span class="grey--text">{{ s.make }} {{ s.model }}</span>
            <span class="grey--text" v-if="s.drivers && s.drivers.length">· {{ s.drivers.join(', ') }}</span>
            <v-spacer></v-spacer>
            <span class="mr-3" :class="s.overdue ? 'error--text font-weight-bold' : ''">Termin: {{ formatDateTime(s.deadline) }}</span>
            <v-btn x-small outlined color="grey darken-1" :loading="cancellingId === s.carId" @click="cancelSummon(s.carId)">Anuluj</v-btn>
          </div>
          <div v-if="s.note" class="mt-1 caption">Notatka: {{ s.note }}</div>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { translateError } from "@/services/translateError";
const axios = require("axios");

export default {
  data() {
    return {
      tab: 0,
      pendingReviews: [],
      summons: [],
      cars: [],
      photoUrls: {},
      loadingReviews: false,
      loadingSummons: false,
      reviewingId: null,
      cancellingId: null,
      summonSaving: false,
      summonForm: { carId: null, deadline: "", note: "" },
      inspectionItems: [
        { key: "exterior", label: "Nadwozie (rysy, wgniecenia)" },
        { key: "interior", label: "Wnętrze" },
        { key: "lights", label: "Światła" },
        { key: "tires", label: "Opony" },
        { key: "documents", label: "Dokumenty (dowód, OC)" },
        { key: "cleanliness", label: "Czystość" },
      ],
    };
  },
  computed: {
    carOptions() {
      return this.cars.map((c) => {
        const driver = c.drivers && c.drivers.length ? ` (${c.drivers[0].firstName} ${c.drivers[0].lastName})` : "";
        return { text: `${c.plate} — ${c.make} ${c.model}${driver}`, value: c.id };
      });
    },
  },
  async created() {
    await Promise.all([this.fetchPendingReviews(), this.fetchSummons(), this.fetchCars()]);
  },
  methods: {
    authHeader() {
      return { Authorization: "Bearer " + localStorage.getItem("token") };
    },
    formatDateTime(d) {
      return d ? new Date(d).toLocaleString("pl-PL") : "—";
    },
    inspMileage(insp) {
      return insp && insp.checklist && insp.checklist.odometer ? insp.checklist.odometer : null;
    },
    inspCl(insp) {
      return (insp && insp.checklist) || {};
    },
    inspPrevOdometer(insp) {
      const v = this.inspCl(insp).previousOdometer;
      return v != null && v > 0 ? v : null;
    },
    inspDelta(insp) {
      const v = this.inspCl(insp).odometerDelta;
      return v != null ? v : null;
    },
    inspAnomaly(insp) {
      return this.inspCl(insp).odometerAnomaly || null; // 'ROLLBACK' | 'BIG_JUMP' | null
    },
    anomalyMeta(insp) {
      const a = this.inspAnomaly(insp);
      if (a === 'ROLLBACK') return { color: 'red darken-1', icon: 'mdi-backup-restore', label: 'Cofnięty licznik' };
      if (a === 'BIG_JUMP') {
        const d = this.inspDelta(insp);
        return { color: 'deep-orange darken-1', icon: 'mdi-trending-up', label: `Duży skok +${d} km` };
      }
      return null;
    },
    ratingMeta(v) {
      if (v === "VERY_GOOD") return { label: "Bardzo dobry", color: "green darken-1" };
      if (v === "GOOD") return { label: "Dobry", color: "amber darken-2" };
      if (v === "BAD") return { label: "Zły", color: "red darken-1" };
      if (v === true) return { label: "OK", color: "green darken-1" };
      if (v === false) return { label: "Nie", color: "grey" };
      return null;
    },
    inspRatings(insp) {
      const cl = (insp && insp.checklist) || {};
      const out = [];
      this.inspectionItems.forEach((it) => {
        if (cl[it.key] === undefined || cl[it.key] === null) return;
        const meta = this.ratingMeta(cl[it.key]);
        if (meta) out.push({ name: it.label, label: meta.label, color: meta.color });
      });
      return out;
    },
    async fetchPendingReviews() {
      this.loadingReviews = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "car-inspections/pending-review",
          headers: this.authHeader(),
        });
        const list = (res.data && res.data.data) || [];
        // Anomalie przebiegu na górę kolejki (najpierw cofnięcia, potem skoki).
        const rank = (i) => (this.inspAnomaly(i) === 'ROLLBACK' ? 0 : this.inspAnomaly(i) === 'BIG_JUMP' ? 1 : 2);
        list.sort((a, b) => rank(a) - rank(b));
        this.pendingReviews = list;
        this.loadPhotos();
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingReviews = false;
      }
    },
    async loadPhotos() {
      const ids = [];
      this.pendingReviews.forEach((insp) => (insp.photos || []).forEach((id) => { if (id && !this.photoUrls[id]) ids.push(id); }));
      for (const id of ids) {
        try {
          const res = await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + id,
            responseType: "blob",
            headers: this.authHeader(),
          });
          this.$set(this.photoUrls, id, window.URL.createObjectURL(res.data));
        } catch (e) { /* pojedyncze błędy zdjęć pomijamy */ }
      }
    },
    async review(id, decision) {
      this.reviewingId = id;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "car-inspections/" + id + "/review",
          headers: this.authHeader(),
          data: { decision },
        });
        await this.fetchPendingReviews();
      } catch (e) {
        Swal.fire("Błąd", translateError(e), "error");
      } finally {
        this.reviewingId = null;
      }
    },
    async fetchSummons() {
      this.loadingSummons = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "car-inspections/active-summons",
          headers: this.authHeader(),
        });
        this.summons = (res.data && res.data.data) || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingSummons = false;
      }
    },
    async fetchCars() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars?take=10000&skip=1&search=",
          headers: this.authHeader(),
        });
        const result = (res.data && res.data.data && res.data.data.result) || [];
        // Wezwanie dotyczy tylko aut firmowych.
        this.cars = result.filter((c) => c.isCompanyCar !== false);
      } catch (e) {
        console.log(e);
      }
    },
    async submitSummon() {
      if (!this.summonForm.carId || !this.summonForm.deadline) return;
      const deadlineIso = new Date(this.summonForm.deadline).toISOString();
      this.summonSaving = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "car-inspections/summon-office/" + this.summonForm.carId,
          headers: this.authHeader(),
          data: { deadline: deadlineIso, note: this.summonForm.note || null },
        });
        Swal.fire({ title: "Wezwanie wysłane", text: "Kierowca otrzymał powiadomienie.", icon: "success", timer: 1800, showConfirmButton: false });
        this.summonForm = { carId: null, deadline: "", note: "" };
        await this.fetchSummons();
      } catch (e) {
        Swal.fire("Błąd", translateError(e), "error");
      } finally {
        this.summonSaving = false;
      }
    },
    async cancelSummon(carId) {
      const ok = await Swal.fire({ title: "Anulować wezwanie?", icon: "question", showCancelButton: true, confirmButtonText: "Tak, anuluj", cancelButtonText: "Nie" });
      if (!ok.isConfirmed) return;
      this.cancellingId = carId;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "car-inspections/cancel-summon/" + carId,
          headers: this.authHeader(),
        });
        await this.fetchSummons();
      } catch (e) {
        Swal.fire("Błąd", translateError(e), "error");
      } finally {
        this.cancellingId = null;
      }
    },
  },
};
</script>

<style scoped>
.inspections-page {
  padding: 16px;
}
/* Inspekcja z anomalią przebiegu — czerwona ramka + delikatne tło, żeby rzucała się w oczy */
.insp-anomaly {
  border-color: #E53935 !important;
  border-width: 2px !important;
  background: rgba(229, 57, 53, 0.04) !important;
}
.page-header {
  margin-bottom: 16px;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
}
</style>
